/**
 * Buildings Cost charts for the Policy Impact Tool.
 * Uses outputs from buildingsCostModel (incremental vs BAU); no physical calculations here.
 */
import React, { useMemo } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  BarElement,
  BarController,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import type { BuildingsCostResults, CostBreakdownByPolicy, CostEffectivenessMetrics } from '../../services/buildingsCostModel';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Tooltip,
  Legend
);

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const shortCurrency = (v: number) => (v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : currencyFormatter.format(v));
/** Format axis ticks without scientific notation. Chart.js scale callback signature. */
const axisTick = (tickValue: string | number): string | number =>
  typeof tickValue === 'number' ? tickValue.toLocaleString(undefined, { maximumFractionDigits: 0 }) : tickValue;

export interface BuildingsCostChartsProps {
  costResults: BuildingsCostResults | null;
}

/** Annual Building Costs and Savings */
export const BuildingsAnnualCostSavingsChart: React.FC<BuildingsCostChartsProps> = ({ costResults }) => {
  const { labels, capitalCost, savings, netCashFlow } = useMemo(() => {
    if (!costResults?.annualCapitalCostSeries?.length) {
      return { labels: [] as string[], capitalCost: [], savings: [], netCashFlow: [] };
    }
    const rows = costResults.annualCapitalCostSeries;
    return {
      labels: rows.map((r) => String(r.year)),
      capitalCost: rows.map((r) => r.value),
      savings: costResults.annualSavingsSeries.map((r) => r.value),
      netCashFlow: costResults.annualNetCashFlowSeries.map((r) => r.value),
    };
  }, [costResults]);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        { label: 'Annual capital exp.', data: capitalCost, borderColor: 'rgb(239, 68, 68)', backgroundColor: 'rgba(239, 68, 68, 0.1)', yAxisID: 'y', tension: 0.2, pointRadius: 2 },
        { label: 'Annual utility bill savings', data: savings, borderColor: 'rgb(34, 197, 94)', backgroundColor: 'rgba(34, 197, 94, 0.1)', yAxisID: 'y', tension: 0.2, pointRadius: 2 },
        { label: 'Annual net cash flow', data: netCashFlow, borderColor: 'rgb(59, 130, 246)', backgroundColor: 'rgba(59, 130, 246, 0.1)', yAxisID: 'y', tension: 0.2, pointRadius: 2 },
      ],
    }),
    [labels, capitalCost, savings, netCashFlow]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${shortCurrency(Number(ctx.parsed.y))}`,
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: { title: { display: true, text: '$' }, min: undefined, ticks: { callback: axisTick } },
      },
    }),
    []
  );

  if (!costResults || labels.length === 0) return null;
  const hasData = capitalCost.some((v) => Math.abs(v) >= 1) || savings.some((v) => Math.abs(v) >= 1) || netCashFlow.some((v) => Math.abs(v) >= 1);
  if (!hasData) return null;

  return (
    <div className="building-electrification-chart-card" style={{ marginTop: 16 }}>
      <h5>Annual Building Costs and Savings</h5>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 8 }}>
        Annual capital exp., utility bill savings vs BAU (gas + building electricity; no EV), and net cash flow.
      </p>
      <div className="building-electrification-chart-container" style={{ height: 260 }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

/** Cumulative Net Cost */
export const BuildingsCumulativeNetCostChart: React.FC<BuildingsCostChartsProps> = ({ costResults }) => {
  const { labels, values } = useMemo(() => {
    if (!costResults?.cumulativeNetCostSeries?.length) {
      return { labels: [] as string[], values: [] as number[] };
    }
    const rows = costResults.cumulativeNetCostSeries;
    return {
      labels: rows.map((r) => String(r.year)),
      values: rows.map((r) => r.value),
    };
  }, [costResults]);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        { label: 'Cumulative net cost', data: values, borderColor: 'rgb(124, 58, 237)', backgroundColor: 'rgba(124, 58, 237, 0.1)', fill: true, tension: 0.2, pointRadius: 2 },
      ],
    }),
    [labels, values]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${shortCurrency(Number(ctx.parsed.y))}`,
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: { title: { display: true, text: 'Cumulative $' }, min: 0, ticks: { callback: axisTick } },
      },
    }),
    []
  );

  if (!costResults || labels.length === 0) return null;
  const hasData = values.some((v) => Math.abs(v) >= 1);
  if (!hasData) return null;

  return (
    <div className="building-electrification-chart-card" style={{ marginTop: 16 }}>
      <h5>Cumulative Net Cost</h5>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 8 }}>
        Cumulative net cost over time (capital exp. minus operating savings).
      </p>
      <div className="building-electrification-chart-container" style={{ height: 240 }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

const BREAKDOWN_KEYS: (keyof CostBreakdownByPolicy)[] = [
  'weatherization',
  'residentialReach',
  'commercialReach',
  'demandResponse',
  'electrification',
  'other',
  'incentives',
];
const BREAKDOWN_LABELS: Record<keyof CostBreakdownByPolicy, string> = {
  weatherization: 'Weatherization',
  residentialReach: 'Residential reach',
  commercialReach: 'Commercial reach',
  demandResponse: 'Demand response',
  electrification: 'Electrification',
  other: 'Other efficiency (est.)',
  incentives: 'Incentives (rebates)',
};
const BREAKDOWN_COLORS: Record<keyof CostBreakdownByPolicy, string> = {
  weatherization: 'rgb(34, 197, 94)',
  residentialReach: 'rgb(59, 130, 246)',
  commercialReach: 'rgb(168, 85, 247)',
  demandResponse: 'rgb(251, 146, 60)',
  electrification: 'rgb(239, 68, 68)',
  other: 'rgb(156, 163, 175)', // Muted gray for placeholder estimate
  incentives: 'rgb(34, 197, 94)',
};

/** Cost Breakdown by Policy */
export const BuildingsCostBreakdownChart: React.FC<BuildingsCostChartsProps> = ({ costResults }) => {
  const { labels, values, colors, backgroundAlpha } = useMemo(() => {
    if (!costResults?.costBreakdownByPolicy) {
      return { labels: [] as string[], values: [] as number[], colors: [] as string[], backgroundAlpha: [] as number[] };
    }
    const b = costResults.costBreakdownByPolicy;
    const labels = BREAKDOWN_KEYS.map((k) => BREAKDOWN_LABELS[k]);
    const values = BREAKDOWN_KEYS.map((k) => b[k] ?? 0);
    const colors = BREAKDOWN_KEYS.map((k) => BREAKDOWN_COLORS[k]);
    const backgroundAlpha = BREAKDOWN_KEYS.map((k) => (k === 'other' ? 0.5 : 1));
    return { labels, values, colors, backgroundAlpha };
  }, [costResults]);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Cost',
          data: values,
          backgroundColor: colors.map((c, i) => {
            const rgb = c.replace('rgb(', '').replace(')', '').trim();
            return `rgba(${rgb}, ${backgroundAlpha[i]})`;
          }),
          borderColor: colors.map((c) => c.replace('rgb', 'rgba').replace(')', ', 0.9)').replace(' ', ', ')),
          borderWidth: 1,
        },
      ],
    }),
    [labels, values, colors, backgroundAlpha]
  );

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y' as const,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.label}: ${shortCurrency(Number(ctx.parsed.x))}`,
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Cumulative cost ($)' }, ticks: { callback: axisTick } },
        y: { title: { display: true, text: 'Policy' } },
      },
    }),
    []
  );

  if (!costResults || labels.length === 0) return null;
  const totalBreakdown = values.reduce((s, v) => s + Math.abs(v), 0);
  if (totalBreakdown < 1) return null;

  return (
    <div className="building-electrification-chart-card" style={{ marginTop: 16 }}>
      <h5>Cost Breakdown by Policy</h5>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 8 }}>
        Cumulative capital exp. by policy area (incentives reduce net cost).
      </p>
      <div className="building-electrification-chart-container" style={{ height: 220 }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

/** Undiscounted cost per ton CO2e reduced */
export const BuildingsCostPerTonChart: React.FC<{ metrics: CostEffectivenessMetrics | null }> = ({ metrics }) => {
  const value = metrics?.costPerTonCO2eReduced ?? null;
  const chartData = useMemo(
    () => ({
      labels: ['Undiscounted cost per ton reduced'],
      datasets: [
        {
          label: 'Undiscounted $/tCO₂e',
          data: [value != null ? value : 0],
          backgroundColor: value != null && value >= 0 ? 'rgba(59, 130, 246, 0.7)' : 'rgba(107, 114, 128, 0.5)',
          borderColor: value != null && value >= 0 ? 'rgb(59, 130, 246)' : 'rgb(107, 114, 128)',
          borderWidth: 1,
        },
      ],
    }),
    [value]
  );

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => (value != null ? `Undiscounted: ${shortCurrency(Number(ctx.parsed.x))} per tCO₂e reduced` : 'No reduction'),
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Undiscounted $/tCO₂e reduced' }, ticks: { callback: axisTick } },
        y: { beginAtZero: true },
      },
    }),
    [value]
  );

  if (metrics == null || value == null) return null;

  return (
    <div className="building-electrification-chart-card" style={{ marginTop: 16 }}>
      <h5>Undiscounted Cost per Ton Reduced (buildings only)</h5>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 8 }}>
        Undiscounted cumulative net cost ÷ <strong>building</strong> emissions reduction (MT CO₂e). Buildings sector only; not discounted. For portfolio cost per ton (all sectors), see Scenario Cost Comparison below.
      </p>
      <div className="building-electrification-chart-container" style={{ height: 120 }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

/** BAU vs Policy Utility Bills */
export const BuildingsUtilityBillChart: React.FC<BuildingsCostChartsProps> = ({ costResults }) => {
  const { labels, bauBill, policyBill } = useMemo(() => {
    if (!costResults?.bauBillByYear?.length) {
      return { labels: [] as string[], bauBill: [] as number[], policyBill: [] as number[] };
    }
    const bau = costResults.bauBillByYear;
    return {
      labels: bau.map((r) => String(r.year)),
      bauBill: bau.map((r) => r.value),
      policyBill: costResults.policyBillByYear.map((r) => r.value),
    };
  }, [costResults]);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        { label: 'BAU utility bill', data: bauBill, borderColor: 'rgb(107, 114, 128)', backgroundColor: 'rgba(107, 114, 128, 0.1)', borderDash: [6, 4], tension: 0.2, pointRadius: 2 },
        { label: 'Policy utility bill', data: policyBill, borderColor: 'rgb(59, 130, 246)', backgroundColor: 'rgba(59, 130, 246, 0.1)', tension: 0.2, pointRadius: 2 },
      ],
    }),
    [labels, bauBill, policyBill]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${shortCurrency(Number(ctx.parsed.y))}`,
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: { title: { display: true, text: 'Annual bill ($)' }, min: 0, ticks: { callback: axisTick } },
      },
    }),
    []
  );

  if (!costResults || labels.length === 0) return null;
  const hasPolicyImpact = bauBill.some((b, i) => Math.abs((policyBill[i] ?? 0) - b) >= 1);
  if (!hasPolicyImpact) return null;

  return (
    <div className="building-electrification-chart-card" style={{ marginTop: 16 }}>
      <h5>Annual Utility Bill Impact</h5>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 8 }}>
        BAU vs policy annual building utility bills (gas + electricity).
      </p>
      <div className="building-electrification-chart-container" style={{ height: 240 }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

/** Electricity vs natural gas cost: BAU vs selected scenario by year */
export const BuildingsElectricityVsGasChart: React.FC<BuildingsCostChartsProps> = ({ costResults }) => {
  const { labels, bauGas, bauElec, policyGas, policyElec } = useMemo(() => {
    if (
      !costResults?.bauGasCostByYear?.length ||
      !costResults?.bauElecCostByYear?.length ||
      !costResults?.policyGasCostByYear?.length ||
      !costResults?.policyElecCostByYear?.length
    ) {
      return { labels: [] as string[], bauGas: [], bauElec: [], policyGas: [], policyElec: [] };
    }
    const rows = costResults.bauGasCostByYear;
    return {
      labels: rows.map((r) => String(r.year)),
      bauGas: rows.map((r) => r.value),
      bauElec: costResults.bauElecCostByYear.map((r) => r.value),
      policyGas: costResults.policyGasCostByYear.map((r) => r.value),
      policyElec: costResults.policyElecCostByYear.map((r) => r.value),
    };
  }, [costResults]);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        { label: 'BAU natural gas', data: bauGas, borderColor: 'rgb(234, 88, 12)', backgroundColor: 'rgba(234, 88, 12, 0.1)', borderDash: [6, 4], yAxisID: 'y', tension: 0.2, pointRadius: 2 },
        { label: 'BAU electricity', data: bauElec, borderColor: 'rgb(59, 130, 246)', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderDash: [6, 4], yAxisID: 'y', tension: 0.2, pointRadius: 2 },
        { label: 'Scenario natural gas', data: policyGas, borderColor: 'rgb(234, 88, 12)', backgroundColor: 'rgba(234, 88, 12, 0.1)', yAxisID: 'y', tension: 0.2, pointRadius: 2 },
        { label: 'Scenario electricity', data: policyElec, borderColor: 'rgb(59, 130, 246)', backgroundColor: 'rgba(59, 130, 246, 0.1)', yAxisID: 'y', tension: 0.2, pointRadius: 2 },
      ],
    }),
    [labels, bauGas, bauElec, policyGas, policyElec]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${shortCurrency(Number(ctx.parsed.y))}`,
          },
        },
      },
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: { title: { display: true, text: 'Annual cost ($)' }, min: 0, ticks: { callback: axisTick } },
      },
    }),
    []
  );

  if (!costResults || labels.length === 0) return null;

  return (
    <div className="building-electrification-chart-card" style={{ marginTop: 16 }}>
      <h5>Electricity vs Natural Gas Cost: BAU vs Scenario</h5>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 8 }}>
        Building energy cost by fuel under selected BAU pathway vs selected scenario. Dashed = BAU; solid = scenario. With only building electrification (Energy Efficiency 0%), scenario electricity cost should sit above BAU and the two lines should diverge as more gas heating shifts to electric. At 40¢/kWh, heat pump operating cost exceeds gas, so total scenario cost is capital plus higher operating cost.
      </p>
      <div className="building-electrification-chart-container" style={{ height: 260 }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};
